// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Checkout  (route /checkout)
// Mobile-first checkout: progress strip → delivering-to card (the customer's DEFAULT saved address + delivery notes, with
// Change → the address book; guests / no saved address fill the fields in) → items (collapsible) → welcome coupon
// (apply / remove; only the account's own, unused coupon) → bill (item total, cut prep, delivery fee, coupon, to pay) →
// wallet credit (ZenFresh credits ledger; applied first, Razorpay pays only the rest, or nothing when the wallet covers it)
// → payment (Razorpay: server-created order + signature check, utils/payments.ts) → sticky "Pay ₹total" bar. The payment / order / email / coupon-redeem logic is unchanged: Razorpay
// outcome → order written to Firestore (status payment_reported_unverified until verification exists) → confirmation
// email (no-op unless EmailJS is configured) → coupon marked used → cart cleared.
// Deliberately NOT here (nothing supports it): delivery-slot picker and "landed at dock" slot copy (no slot data or order
// field), express cold-chain / sub-zero packaging / 0 °C claims, timers, ₹ savings on markdown/strike-through prices,
// wallet and Pay-on-Inspection (Razorpay is the only method), SSL / 256-bit / "odor & chemical free" assurances.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, Clock, Lock, MapPin, ShoppingBag, Tag, Wallet as WalletIcon } from "lucide-react";
import PageTransition from "../components/PageTransition";
import HoneypotField from "../components/HoneypotField";
import { packPrice } from "../data/packs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useAuth from "../hooks/useAuth";
import useCart from "../hooks/useCart";
import { fetchAddresses, type Address } from "../utils/addresses";
import useZones from "../hooks/useZones";
import { findActiveZone } from "../utils/zones";
import useCommercialRules from "../hooks/useCommercialRules";
import { deliveryFeeFor } from "../utils/finance";
import { createOrder } from "../utils/orders";
import { fetchProfile } from "../utils/profile";
import { ApiError } from "../utils/api";
import { fetchPaymentConfig } from "../utils/payments";
import { fetchWallet, type Wallet } from "../utils/wallet";
import { fetchWelcomeCoupon, redeemWelcomeCoupon, type WelcomeCoupon } from "../utils/coupon";
import { useAppSettings } from "../hooks/useContactInfo";
import { checkPromotion, describeBenefit, fetchPublicOffers, type PromoCheck, type PublicOffer } from "../utils/promotions";
import { formatInr } from "../utils/priceFormat";
import { generateOrderReference } from "../utils/orderReference";
import { openRazorpayCheckout } from "../utils/razorpayCheckout";
import { writeOrder, type OrderStatus } from "../utils/firebaseClient";
import { sendOrderConfirmationEmail } from "../utils/emailNotifications";
import { trackEvent } from "../utils/analytics";


const INPUT_CLASS =
  "mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50";

type Stage = "form" | "paying" | "done";

const ADDRESS_LABELS = { home: "Home", work: "Work", other: "Friends & family" } as const;

const STATUS_COPY: Record<OrderStatus, { title: string; body: string }> = {
  payment_reported_unverified: {
    title: "Order received — confirmation pending",
    body: "Your payment was reported by Razorpay's checkout, but hasn't been verified by our systems yet (we're still setting up automated verification). Our team will confirm it manually and reach out if anything's off.",
  },
  payment_failed: {
    title: "Payment failed",
    body: "Razorpay reported that this payment didn't go through. Your order wasn't placed — no charge should appear on your account. Please try again.",
  },
  payment_cancelled: {
    title: "Payment cancelled",
    body: "You closed the payment window before it completed. Your order wasn't placed.",
  },
};

export default function Checkout() {
  useDocumentTitle("Checkout");
  const { lines, subtotal, clear } = useCart();
  const [honeypot, setHoneypot] = useState("");
  const zones = useZones();
  const rules = useCommercialRules();
  const [stage, setStage] = useState<Stage>("form");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showItems, setShowItems] = useState(true);
  const [saved, setSaved] = useState<{ address: Address | null }>({ address: null });
  const [result, setResult] = useState<{
    orderRef: string | null;
    status: OrderStatus;
    paymentId: string | null;
    verified: boolean;
    testMode: boolean;
  } | null>(null);
  /** From the backend: false = Razorpay test mode (no real money); null = unknown / not configured. */
  const [livemode, setLivemode] = useState<boolean | null>(null);
  // Staff can pause online payments; the customer is told before they try (the backend refuses a new payment anyway).
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    fetchPaymentConfig().then(
      (config) => { setLivemode(config.enabled ? config.livemode : null); setPaused(config.enabled && config.paused === true); },
      () => undefined,
    );
  }, []);

  const auth = useAuth();
  const appSettings = useAppSettings();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState<WelcomeCoupon | null>(null);
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponMessage, setCouponMessage] = useState<string | null>(null);
  // A staff promotion code (Promotions screen): the backend works the discount out; only one code applies at a time.
  const [promo, setPromo] = useState<PromoCheck | null>(null);
  const [offers, setOffers] = useState<PublicOffer[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [useWallet, setUseWallet] = useState(true);

  // A signed-in customer's default saved address (the API lists it first) and profile prefill the contact details.
  useEffect(() => {
    if (auth.status !== "signed-in") return;
    let cancelled = false;
    Promise.all([fetchAddresses(), fetchProfile()]).then(
      ([addresses, profile]) => {
        // The email saved on the profile prefills the confirmation address (never over one the customer already typed).
        if (!cancelled && profile.email) setEmail((current) => current || profile.email || "");
        const address = addresses[0];
        if (cancelled || !address || !profile.fullName || !profile.phone) return;
        setSaved({ address });
        setName(profile.fullName);
        setPhone(profile.phone);
        setAddressLine([address.house, address.street, address.landmark].filter(Boolean).join(", "));
        setCity(address.city);
        setPincode(address.pincode);
      },
      () => undefined, // a failed lookup just leaves the manual fields
    );
    return () => {
      cancelled = true;
    };
  }, [auth.status]);
  const usingSaved = saved.address !== null;
  // Delivery zones: staff draw where we deliver. Once one is active, an order needs a saved address whose map pin is inside it.
  const pin = saved.address ? { lat: saved.address.lat, lng: saved.address.lng } : null;
  const zone = pin ? findActiveZone(zones.zones, pin) : null;
  const notes = saved.address
    ? [
        saved.address.instructions.leaveWithSecurity && "Leave with security desk if unreachable",
        saved.address.instructions.directHandover && "Hand over to me directly",
        saved.address.instructions.whatsappBeforeArrival && "WhatsApp me before arriving",
      ].filter((note): note is string => typeof note === "string")
    : [];

  // The customer's wallet credit (a signed-in customer only); applied to the bill unless they untick it.
  useEffect(() => {
    if (auth.status !== "signed-in") return;
    let cancelled = false;
    fetchWallet().then((fetched) => !cancelled && setWallet(fetched), () => undefined);
    return () => {
      cancelled = true;
    };
  }, [auth.status]);

  // Only the account's own, unused coupon can be applied; the backend is the source of truth.
  useEffect(() => {
    if (auth.status !== "signed-in") return;
    let cancelled = false;
    fetchWelcomeCoupon().then(
      (fetched) => !cancelled && setCoupon(fetched.redeemed ? null : fetched),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [auth.status]);

  useEffect(() => {
    let cancelled = false;
    fetchPublicOffers().then((list) => !cancelled && setOffers(list), () => undefined);
    return () => { cancelled = true; };
  }, []);

  // An applied promotion is re-checked whenever the basket changes (its minimum, percentage and cap depend on it).
  const promoCode = promo?.code;
  useEffect(() => {
    if (!promoCode) return;
    let cancelled = false;
    checkPromotion(promoCode, subtotal).then(
      (result) => !cancelled && setPromo(result),
      (e: unknown) => { if (!cancelled) { setPromo(null); setCouponMessage(e instanceof ApiError ? e.message : "That code no longer applies."); } },
    );
    return () => { cancelled = true; };
  }, [promoCode, subtotal]);

  const applyCoupon = async (typed?: string) => {
    const code = (typed ?? couponInput).trim().toUpperCase();
    if (!(coupon && code === coupon.code)) {
      if (auth.status !== "signed-in") { setCouponMessage("Sign in to use a code."); return; }
      try {
        const result = await checkPromotion(code, subtotal);
        setPromo(result); setCouponApplied(false); setCouponInput(code); setCouponMessage(null);
      } catch (e) {
        setCouponMessage(e instanceof ApiError && e.code === "PROMO_REJECTED" ? e.message : "That code isn't valid for this account.");
      }
      return;
    }
    if (subtotal < coupon.minOrderInr) {
      setCouponMessage(`Add ${formatInr(coupon.minOrderInr - subtotal)} more to use this coupon (minimum ${formatInr(coupon.minOrderInr)}).`);
    } else {
      setCouponApplied(true);
      setPromo(null);
      setCouponMessage(null);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(false);
    setPromo(null);
    setCouponInput("");
    setCouponMessage(null);
  };

  // The coupon stops applying if the basket drops below its minimum.
  const couponActive = couponApplied && coupon !== null && subtotal >= coupon.minOrderInr;
  const discount = couponActive ? coupon.amountOffInr : promo ? promo.discount : 0;
  // The site-wide fee and free-delivery threshold come from staff (Finance & tax); an active zone's own fee / threshold win.
  const standardFee = deliveryFeeFor(subtotal, zones.enforced ? zone : null, rules);
  const deliveryFee = lines.length === 0 || couponActive || promo?.freeDelivery ? 0 : standardFee;
  const total = Math.max(0, subtotal - discount) + deliveryFee;
  // `subtotal` already includes each cut's prep fee; the bill shows the two parts apart (both real).
  const catchTotal = lines.reduce((sum, line) => sum + packPrice(line.item, line.pack) * line.qty, 0);
  const prepTotal = subtotal - catchTotal;
  // What the coupon really saved: its amount off plus the delivery fee it waives.
  const savedTotal = couponActive ? discount + standardFee : promo ? promo.discount + (promo.freeDelivery ? standardFee : 0) : 0;
  // Wallet credit is used first; Razorpay is asked only for what is left (nothing at all if the wallet covers it).
  const walletApplied = auth.status === "signed-in" && useWallet && wallet ? Math.min(wallet.balance, total) : 0;
  const toPay = total - walletApplied;
  // Why we can't take this order yet (the backend enforces the same rules): shown above the pay button, which stays disabled.
  // Staff can pause the whole storefront (Admin → App settings): the backend refuses new orders and payments, this says why.
  const storefrontBlock = appSettings?.maintenance.enabled ? `${appSettings.maintenance.headline}${appSettings.maintenance.resumption ? ` Expected back: ${appSettings.maintenance.resumption}.` : ""} New orders are paused for now.` : null;
  const paymentsBlock = paused && toPay > 0 ? "Online payments are paused for a short while. Please try again later, or pay from your wallet if it covers the order." : null;
  const pausedBlock = storefrontBlock ?? paymentsBlock;
  const zoneBlock = !zones.enforced || lines.length === 0 ? null
    : pin === null ? "We deliver only inside our delivery areas. Sign in and save a delivery address with a map pin so we can check yours."
    : zone === null ? "We don't deliver to your saved address yet. Choose another address in your address book."
    : subtotal < zone.minOrder ? `The minimum order in ${zone.name} is ${formatInr(zone.minOrder)}. Add ${formatInr(zone.minOrder - subtotal)} more.`
    : null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (honeypot || lines.length === 0) return;

    setError(null);
    setStage("paying");
    trackEvent("checkout_started", { item_count: lines.length });

    // Ask the backend once more BEFORE any money moves: a code that ran out or expired meanwhile must not be found out after paying.
    if (promo) {
      try {
        const fresh = await checkPromotion(promo.code, subtotal);
        if (fresh.discount !== promo.discount || fresh.freeDelivery !== promo.freeDelivery) throw new Error("changed");
      } catch (e) {
        setPromo(null);
        setStage("form");
        setError(e instanceof ApiError && e.code === "PROMO_REJECTED" ? `${e.message} The code was removed; check the total and pay again.` : "That code changed while you were checking out. It was removed; check the total and pay again.");
        return;
      }
    }

    // The reference is fixed BEFORE paying so the Razorpay order carries it as its receipt.
    const plannedRef = generateOrderReference();
    const orderInput = (payment: { razorpayOrderId: string; paymentId: string } | null) => ({
      orderRef: plannedRef,
      ...(payment ?? {}),
      items: lines.map((line) => ({ id: line.item.id, name: line.item.name, pack: line.pack.id, cut: line.cut.name, qty: line.qty, unitPrice: line.unitPrice, lineTotal: line.lineTotal })),
      subtotal,
      deliveryFee,
      discount,
      couponCode: couponActive && coupon ? coupon.code : promo ? promo.code : null,
      total,
      walletAmount: walletApplied,
      delivery: { name, phone, addressLine, city, pincode, notes, ...(pin ?? {}) },
    });

    // The wallet covers the whole order: no payment window at all, the order is created straight from the wallet.
    if (walletApplied > 0 && toPay === 0) {
      try {
        const created = await createOrder(orderInput(null));
        trackEvent("checkout_completed", { order_ref: created.ref });
        if (couponActive && coupon) void redeemWelcomeCoupon(coupon.code).catch(() => undefined);
        clear();
        navigate(`/orders/${created.ref}`, { replace: true, state: { justPlaced: true } });
      } catch (e) {
        setStage("form");
        setError(
          e instanceof ApiError && (e.code === "NOT_SERVICEABLE" || e.code === "BELOW_MINIMUM_ORDER" || e.code === "STOREFRONT_PAUSED" || e.code === "PROMO_REJECTED" || e.code === "ORDER_TOTALS_INVALID") ? e.message
          : e instanceof ApiError && e.code === "WALLET_INSUFFICIENT"
            ? "Your wallet balance changed, so it no longer covers this order. Reload and check the total."
            : "We couldn't place this order. Check your connection and try again.",
        );
      }
      return;
    }

    const outcome = await openRazorpayCheckout({
      amountInr: Math.round(toPay),
      receipt: plannedRef,
      customerName: name,
      customerEmail: email,
      customerPhone: phone,
      delivery: pin ? { ...pin, subtotal } : undefined,
    });

    if (outcome.outcome === "refused") {
      setStage("form");
      setError(outcome.reason ?? "We can't deliver this order to that address.");
      return;
    }

    if (outcome.outcome === "unconfigured" || outcome.outcome === "load_error") {
      setStage("form");
      setError(
        outcome.outcome === "unconfigured"
          ? "Online payment isn't set up yet — please contact us via WhatsApp or the Contact page to place this order."
          : "Couldn't load the payment window. Check your connection and try again."
      );
      return;
    }

    const status: OrderStatus =
      outcome.outcome === "success"
        ? "payment_reported_unverified"
        : outcome.outcome === "failed"
          ? "payment_failed"
          : "payment_cancelled";

    const orderItems = lines.map((line) => ({
      id: line.item.id,
      // The pack size and cut are part of the ordered item (its unit price is the price of one pack, prep fee included).
      name: `${line.item.name} (${line.pack.label}, ${line.cut.name})`,
      unitPrice: line.unitPrice,
      qty: line.qty,
      lineTotal: line.lineTotal,
    }));
    const paymentId = outcome.outcome === "success" ? (outcome.paymentId ?? null) : null;

    // generateOrderReference()'s 8-digit suffix makes a doc-ID collision
    // rare, not impossible — Firestore rules treat a second `set()` on an
    // existing doc ID as an "update" (denied), so a collision throws here.
    // One retry with a freshly generated ref is enough to make the residual
    // risk negligible; if that also fails, this is honestly surfaced below
    // rather than silently swallowed and shown as if it were logged.
    let orderRef: string | null = null;
    for (let attempt = 0; attempt < 2 && orderRef === null; attempt++) {
      const candidateRef = attempt === 0 ? plannedRef : generateOrderReference();
      try {
        await writeOrder({
          orderRef: candidateRef,
          status,
          items: orderItems,
          subtotal,
          deliveryFee,
          total,
          paymentId,
          contact: { name, phone, email, addressLine, city, pincode },
        });
        orderRef = candidateRef;
      } catch {
        // Falls through to the next attempt, or to the loggingFailed path below.
      }
    }

    // A signed-in customer's VERIFIED payment also becomes an order in My Orders (the backend re-checks the payment,
    // its receipt and its amount before creating it). If that fails the payment is still recorded above.
    let accountRef: string | null = null;
    if (outcome.outcome === "success" && outcome.verified && outcome.gatewayOrderId && outcome.paymentId && auth.status === "signed-in") {
      try {
        const created = await createOrder(orderInput({ razorpayOrderId: outcome.gatewayOrderId, paymentId: outcome.paymentId }));
        accountRef = created.ref;
      } catch {
        // Falls through to the result screen below, which still shows the payment and the reference.
      }
    }

    const recordedRef = orderRef ?? accountRef;
    if (recordedRef && status === "payment_reported_unverified") {
      trackEvent("checkout_completed", { order_ref: recordedRef });
      void sendOrderConfirmationEmail({ orderRef: recordedRef, customerName: name, customerEmail: email, items: orderItems, total });
      if (couponActive && coupon) void redeemWelcomeCoupon(coupon.code).catch(() => undefined);
      clear();
    }

    if (accountRef) {
      navigate(`/orders/${accountRef}`, { replace: true, state: { justPlaced: true } });
      return;
    }

    setResult({ orderRef, status, paymentId, verified: outcome.verified === true, testMode: outcome.testMode === true });
    setStage("done");
  };

  if (stage === "done" && result) {
    const succeeded = result.status === "payment_reported_unverified" && result.orderRef !== null;
    // Payment was reported by Razorpay, but we couldn't log it in Firestore
    // even after a retry (see the retry loop above) — the rarest, most
    // important case to get right: never show this as a normal success
    // (there's no order on file for support to find), and never lose the
    // one piece of evidence the customer has, their Razorpay payment ID.
    const loggingFailed = result.status === "payment_reported_unverified" && result.orderRef === null;
    const verified = succeeded && result.verified;
    const copy = verified
      ? {
          title: "Payment confirmed — order received",
          body: "Razorpay confirmed your payment and we've recorded your order. Keep the reference below; our team will be in touch about your delivery.",
        }
      : loggingFailed
      ? {
          title: "Payment reported — order not saved",
          body: "Razorpay reported your payment went through, but we hit a technical problem saving your order. Please contact us via WhatsApp or the Contact page right away with the payment ID below so our team can find and confirm it manually.",
        }
      : STATUS_COPY[result.status];
    return (
      <PageTransition>
        {/* Full-height dark section, not the light-with-dark: pattern the
            form/summary section below uses — the fixed Navbar's text is
            unconditionally text-foam-50 (white), expecting a dark or
            transparent-over-dark-hero background the same way every other
            page's hero band provides. A short single-message screen like
            this has no room for a separate hero band above it, so the whole
            section carries the dark background instead (same fix NotFound.tsx
            already uses for the same reason). */}
        <section className="flex min-h-screen flex-col items-center justify-center bg-ocean-950 px-6 py-24 text-center text-foam-50">
          {succeeded || loggingFailed ? (
            <Clock className="h-14 w-14 text-teal-300" />
          ) : (
            <ShoppingBag className="h-14 w-14 text-foam-100/30" />
          )}
          <h1 className="mt-5 font-display text-2xl font-bold">{copy.title}</h1>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-foam-100/70">{copy.body}</p>
          {result.testMode && (
            <p className="mt-3 rounded-lg bg-white/10 px-3 py-1.5 text-xs text-foam-100/80">Test payment — no real money was charged.</p>
          )}
          {succeeded && result.orderRef && (
            <p className="mt-4 rounded-xl bg-white/10 px-4 py-2 font-mono text-sm text-foam-50 ring-1 ring-white/15">
              {result.orderRef}
            </p>
          )}
          {loggingFailed && result.paymentId && (
            <p className="mt-4 rounded-xl bg-white/10 px-4 py-2 font-mono text-sm text-foam-50 ring-1 ring-white/15">
              Payment ID: {result.paymentId}
            </p>
          )}
          <Link
            to={succeeded ? "/" : loggingFailed ? "/contact" : "/checkout"}
            onClick={() => !succeeded && !loggingFailed && setStage("form")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal-400 px-6 py-3 text-sm font-semibold text-ocean-950 transition-transform hover:scale-105"
          >
            {succeeded ? "Back to Home" : loggingFailed ? "Contact Us" : "Try Again"}
          </Link>
        </section>
      </PageTransition>
    );
  }

  if (lines.length === 0) {
    return (
      <PageTransition>
        {/* Same full-height-dark fix as the "done" branch above, and for the
            same reason — this screen has no hero band of its own for the
            fixed Navbar's white text to sit on. */}
        <section className="flex min-h-screen flex-col items-center justify-center bg-ocean-950 px-6 py-24 text-center text-foam-50">
          <ShoppingBag className="h-12 w-12 text-foam-100/30" />
          <h1 className="mt-4 font-display text-xl font-bold">Your cart is empty</h1>
          <p className="mt-2 text-sm text-foam-100/70">Add something fresh from the menu first.</p>
          <Link
            to="/menu"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-teal-400 px-6 py-3 text-sm font-semibold text-ocean-950 transition-transform hover:scale-105"
          >
            Browse the Menu
          </Link>
        </section>
      </PageTransition>
    );
  }

  const steps = ["Cart", "Delivery", "Payment"];

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-44 pt-28 dark:bg-ocean-950 sm:pt-32">
        <form id="checkout-form" onSubmit={handleSubmit} className="mx-auto flex max-w-xl flex-col gap-4 px-4">
          <HoneypotField value={honeypot} onChange={setHoneypot} />

          <header className="flex items-center gap-3">
            <Link
              to="/menu"
              aria-label="Back to the menu"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">Checkout</h1>
          </header>

          <ol aria-label="Checkout progress" className="flex items-center justify-between rounded-2xl bg-ocean-800 px-4 py-3 text-white">
            {steps.map((step, index) => (
              <li key={step} aria-current={index === 1 ? "step" : undefined} className="flex items-center gap-2 text-sm font-semibold">
                <span
                  aria-hidden="true"
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${index === 0 ? "bg-teal-400 text-ocean-950" : index === 1 ? "bg-white text-ocean-900" : "bg-white/25"}`}
                >
                  {index === 0 ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>
                <span className={index === 2 ? "text-white/80" : ""}>{step}</span>
              </li>
            ))}
          </ol>

          <section aria-labelledby="deliver-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
            <div className="flex items-center justify-between gap-2">
              <h2 id="deliver-heading" className="flex items-center gap-2 font-display text-lg font-bold text-ocean-950 dark:text-foam-50">
                <MapPin className="h-5 w-5 text-teal-700 dark:text-teal-300" aria-hidden="true" /> Delivering fresh catch to
              </h2>
              {auth.status === "signed-in" && (
                <Link to="/account/addresses" className="text-sm font-bold text-teal-700 underline underline-offset-2 dark:text-teal-300">
                  Change
                </Link>
              )}
            </div>

            {usingSaved && saved.address ? (
              <div className="mt-3 rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5">
                <p className="text-sm">
                  <span className="mr-2 rounded bg-white px-2 py-0.5 text-xs font-bold uppercase text-ocean-900 ring-1 ring-ocean-900/10">{ADDRESS_LABELS[saved.address.label]}</span>
                  <span className="font-bold text-ocean-950 dark:text-foam-50">{name}</span>
                  <span className="text-ocean-800/80 dark:text-foam-100/75"> • {phone}</span>
                </p>
                <p className="mt-1 text-sm text-ocean-800/90 dark:text-foam-100/85">{addressLine}, {city} {pincode}</p>
                {notes.length > 0 && (
                  <p className="mt-2 rounded-lg bg-white px-3 py-2 text-xs text-ocean-800/85 dark:bg-white/10 dark:text-foam-100/80">
                    Your delivery notes: {notes.join(" · ")}. These are requests to the courier.
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                {auth.status !== "signed-in" && (
                  <p className="text-xs text-ocean-800/80 sm:col-span-2 dark:text-foam-100/75">
                    <Link to="/welcome" className="font-semibold text-teal-700 underline underline-offset-2 dark:text-teal-300">Log in</Link> to use a saved address, or fill in the details below.
                  </p>
                )}
                <label className="block text-sm">
                  <span className="font-medium text-ocean-900 dark:text-foam-100">Full name</span>
                  <input required type="text" value={name} onChange={(e) => setName(e.target.value)} className={INPUT_CLASS} />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-ocean-900 dark:text-foam-100">Phone</span>
                  <input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={INPUT_CLASS} />
                </label>
                <label className="block text-sm sm:col-span-2">
                  <span className="font-medium text-ocean-900 dark:text-foam-100">Address</span>
                  <input required type="text" value={addressLine} onChange={(e) => setAddressLine(e.target.value)} className={INPUT_CLASS} />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-ocean-900 dark:text-foam-100">City</span>
                  <input required type="text" value={city} onChange={(e) => setCity(e.target.value)} className={INPUT_CLASS} />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-ocean-900 dark:text-foam-100">Pincode</span>
                  <input required type="text" inputMode="numeric" pattern="[0-9]{6}" title="6-digit pincode" value={pincode} onChange={(e) => setPincode(e.target.value)} className={INPUT_CLASS} />
                </label>
              </div>
            )}

            <label className="mt-3 block text-sm">
              <span className="font-medium text-ocean-900 dark:text-foam-100">Email <span className="font-normal text-ocean-800/70 dark:text-foam-100/65">(for your order confirmation)</span></span>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className={INPUT_CLASS} />
            </label>
          </section>

          <section aria-labelledby="items-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
            <div className="flex items-center justify-between gap-2">
              <h2 id="items-heading" className="flex items-center gap-2 font-display text-lg font-bold text-ocean-950 dark:text-foam-50">
                <ShoppingBag className="h-5 w-5 text-teal-700 dark:text-teal-300" aria-hidden="true" /> Items in order ({lines.length})
              </h2>
              <button
                type="button"
                aria-expanded={showItems}
                aria-controls="order-items"
                onClick={() => setShowItems((open) => !open)}
                className="inline-flex min-h-11 items-center gap-1 rounded-full px-3 text-sm font-bold text-teal-700 hover:bg-teal-100/60 focus-visible:outline-2 focus-visible:outline-teal-600 dark:text-teal-300"
              >
                {showItems ? "Hide details" : "Show details"}
                <ChevronDown className={`h-4 w-4 transition-transform ${showItems ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
            </div>
            <ul id="order-items" hidden={!showItems} className="mt-3 space-y-3">
              {lines.map((line) => (
                <li key={`${line.item.id}-${line.pack.id}`} className="flex gap-3">
                  <img src={line.item.image} alt="" width={64} height={64} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold leading-snug text-ocean-950 dark:text-foam-50">{line.item.name}</p>
                    <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{line.pack.label} · {line.cut.name}</p>
                    <p className="mt-1 flex items-center gap-2 text-sm">
                      <span className="font-bold text-ocean-950 dark:text-foam-50">{formatInr(line.lineTotal)}</span>
                      <span className="rounded bg-teal-100 px-1.5 py-0.5 text-xs font-bold text-teal-700">Qty: {line.qty}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-ocean-800/75 dark:text-foam-100/70">
              Need a change? <Link to="/menu" className="font-semibold text-teal-700 underline underline-offset-2 dark:text-teal-300">Edit your cart</Link> from the menu.
            </p>
          </section>

          {(coupon || couponActive || promo || auth.status === "signed-in") && (
            <section aria-labelledby="coupon-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
              <h2 id="coupon-heading" className="flex items-center gap-2 text-sm font-bold text-ocean-950 dark:text-foam-50">
                <Tag className="h-4 w-4 text-teal-700 dark:text-teal-300" aria-hidden="true" /> Coupon or offer code
              </h2>
              {promo ? (
                <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-teal-100 px-3 py-2" data-testid="promo-applied">
                  <p className="text-sm font-bold text-teal-700">
                    <span className="font-mono tracking-wider">{promo.code}</span> applied — {promo.freeDelivery ? "free delivery" : `${formatInr(promo.discount)} off`}
                  </p>
                  <button type="button" onClick={removeCoupon} className="min-h-11 shrink-0 px-2 text-sm font-bold text-coral-500 hover:underline">
                    Remove
                  </button>
                </div>
              ) : couponActive && coupon ? (
                <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-teal-100 px-3 py-2">
                  <p className="text-sm font-bold text-teal-700">
                    <span className="font-mono tracking-wider">{coupon.code}</span> applied — {formatInr(coupon.amountOffInr)} off and free delivery
                  </p>
                  <button type="button" onClick={removeCoupon} className="min-h-11 shrink-0 px-2 text-sm font-bold text-coral-500 hover:underline">
                    Remove
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <input
                    id="coupon-code"
                    aria-label="Coupon code"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    autoCapitalize="characters"
                    autoComplete="off"
                    aria-describedby="coupon-message"
                    placeholder="Enter your code"
                    className="min-w-0 flex-1 rounded-xl border border-ocean-900/10 bg-foam-50 px-3 py-2 font-mono text-sm uppercase tracking-wider text-ocean-950 outline-none focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                  />
                  <button
                    type="button"
                    onClick={() => void applyCoupon()}
                    disabled={couponInput.trim() === ""}
                    className="min-h-11 rounded-xl bg-teal-700 px-4 text-sm font-bold text-white hover:bg-teal-600 disabled:opacity-50"
                  >
                    Apply
                  </button>
                </div>
              )}
              <p id="coupon-message" role="status" className="mt-1.5 text-xs text-coral-600 dark:text-coral-300">
                {couponMessage}
              </p>
              {!promo && !couponActive && offers.length > 0 && (
                <ul aria-label="Offers you can use" className="mt-2 flex flex-wrap gap-2">
                  {offers.map((o) => (
                    <li key={o.code}>
                      <button type="button" data-testid="offer-chip" onClick={() => void applyCoupon(o.code)} className="min-h-11 rounded-xl bg-teal-100 px-3 py-1.5 text-left text-xs font-bold text-teal-700 hover:bg-teal-200">
                        <span className="font-mono tracking-wider">{o.code}</span> · {describeBenefit(o)}
                        {o.minOrder > 0 && <span className="block font-normal">on {formatInr(o.minOrder)}+</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {wallet && wallet.balance > 0 && (
            <section aria-labelledby="wallet-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
              <h2 id="wallet-heading" className="flex items-center gap-2 text-sm font-bold text-ocean-950 dark:text-foam-50">
                <WalletIcon className="h-4 w-4 text-teal-700 dark:text-teal-300" aria-hidden="true" /> ZenFresh wallet
              </h2>
              <label className="mt-2 flex items-start gap-3 text-sm">
                <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} className="mt-0.5 h-5 w-5 accent-teal-600" />
                <span>
                  <span className="block font-semibold text-ocean-950 dark:text-foam-50">Use my wallet credit ({formatInr(wallet.balance)} available)</span>
                  <span className="block text-ocean-800/80 dark:text-foam-100/75">
                    {useWallet
                      ? walletApplied === total
                        ? `${formatInr(walletApplied)} covers this whole order — no online payment needed.`
                        : `${formatInr(walletApplied)} will be used; you pay the remaining ${formatInr(toPay)} online.`
                      : "Not using it on this order."}
                  </span>
                </span>
              </label>
            </section>
          )}

          <section id="bill-details" aria-labelledby="bill-heading" className="scroll-mt-32 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
            <h2 id="bill-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Bill details</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-ocean-800/85 dark:text-foam-100/80">Item total</dt>
                <dd className="font-semibold text-ocean-950 dark:text-foam-50">{formatInr(catchTotal)}</dd>
              </div>
              {prepTotal > 0 && (
                <div className="flex justify-between">
                  <dt className="text-ocean-800/85 dark:text-foam-100/80">Cut &amp; clean prep</dt>
                  <dd className="font-semibold text-ocean-950 dark:text-foam-50">{formatInr(prepTotal)}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-ocean-800/85 dark:text-foam-100/80">Delivery fee</dt>
                <dd className="font-semibold text-ocean-950 dark:text-foam-50">{deliveryFee === 0 ? "FREE" : formatInr(deliveryFee)}</dd>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-teal-700 dark:text-teal-300">
                  <dt>{promo ? `Code ${promo.code}` : "Welcome coupon"}</dt>
                  <dd className="font-semibold">−{formatInr(discount)}</dd>
                </div>
              )}
              {walletApplied > 0 && (
                <>
                  <div className="flex justify-between border-t border-ocean-900/10 pt-2 dark:border-white/10">
                    <dt className="font-semibold text-ocean-950 dark:text-foam-50">Order total</dt>
                    <dd className="font-semibold text-ocean-950 dark:text-foam-50">{formatInr(total)}</dd>
                  </div>
                  <div className="flex justify-between text-teal-700 dark:text-teal-300">
                    <dt>ZenFresh wallet</dt>
                    <dd className="font-semibold">−{formatInr(walletApplied)}</dd>
                  </div>
                </>
              )}
            </dl>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-ocean-700/5 px-3 py-3 dark:bg-white/5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-ocean-800/80 dark:text-foam-100/75">To pay</p>
                <p className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">{formatInr(toPay)}</p>
              </div>
              {savedTotal > 0 && <span className="rounded-full bg-teal-100 px-3 py-1 text-xs font-bold text-teal-700">You saved {formatInr(savedTotal)}</span>}
            </div>
          </section>

          <section aria-labelledby="pay-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
            <h2 id="pay-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Payment</h2>
            <p className="mt-2 flex items-start gap-3 rounded-xl bg-ocean-700/5 p-3 text-sm dark:bg-white/5">
              <Lock className="mt-0.5 h-5 w-5 shrink-0 text-teal-700 dark:text-teal-300" aria-hidden="true" />
              <span>
                <span className="block font-bold text-ocean-950 dark:text-foam-50">{toPay === 0 ? "Paid from your wallet" : "Pay online with Razorpay"}</span>
                <span className="block text-ocean-800/85 dark:text-foam-100/80">{toPay === 0 ? "Nothing more to pay: your wallet credit covers this order." : "You&apos;ll choose UPI, card or another method in Razorpay&apos;s secure payment window."}</span>
                {livemode === false && (
                  <span className="mt-1 block rounded bg-coral-400/10 px-2 py-1 text-xs font-semibold text-coral-500 dark:text-coral-300">
                    Test mode: no real money is charged. Use Razorpay&apos;s test card or test UPI.
                  </span>
                )}
              </span>
            </p>
          </section>

          {pausedBlock && !zoneBlock && <p role="alert" data-testid="paused-block" className="rounded-xl bg-coral-500/10 p-3 text-center text-sm font-semibold text-coral-700 dark:text-coral-300">{pausedBlock}</p>}
          {zoneBlock && <p role="alert" data-testid="zone-block" className="rounded-xl bg-coral-500/10 p-3 text-center text-sm font-semibold text-coral-700 dark:text-coral-300">{zoneBlock}</p>}
          {error && <p role="alert" className="text-center text-sm font-semibold text-coral-500 dark:text-coral-300">{error}</p>}
          <p className="text-center text-xs text-ocean-800/75 dark:text-foam-100/70">
            ZenFresh is an advance booking platform — availability is subject to the day&apos;s catch. See our{" "}
            <Link to="/terms" className="underline">Terms</Link>.
          </p>
        </form>

        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-ocean-900/10 bg-white px-4 pb-4 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-ocean-900">
          <div className="mx-auto flex max-w-xl items-center justify-between gap-3 pr-16 sm:pr-0">
            <div>
              <p className="font-display text-xl font-bold leading-tight text-ocean-950 dark:text-foam-50">
                {formatInr(toPay)}
                {savedTotal > 0 && <span className="ml-2 text-xs font-bold text-teal-700 dark:text-teal-300">Saved {formatInr(savedTotal)}</span>}
              </p>
              <a href="#bill-details" className="text-xs font-semibold text-teal-700 underline underline-offset-2 dark:text-teal-300">View bill details</a>
            </div>
            <button
              type="submit"
              form="checkout-form"
              disabled={stage === "paying" || zoneBlock !== null || pausedBlock !== null}
              className="inline-flex min-h-12 items-center gap-2 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white transition-colors hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:opacity-50"
            >
              <Lock className="h-4 w-4" aria-hidden="true" />
              {stage === "paying" ? (toPay === 0 ? "Placing order…" : "Opening payment…") : toPay === 0 ? "Place order" : `Pay ${formatInr(toPay)}`}
            </button>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
